/** @jsxImportSource @emotion/react */

import { useLayers } from '@app/contexts/LayerContext'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { useRiskFetcher } from '@app/contexts/RiskContext'
import { css } from '@emotion/react'
import ForumIcon from '@mui/icons-material/Forum'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { RootState } from '@app/redux/store'
import { usePreferences } from '@app/hooks/usePreferences'
import { infoLayerFocusAtom } from '@app/state/layerAtom'
import { showFeedbackModalAtom } from '@app/state/modalAtom'
import { useAtom, useSetAtom } from 'jotai'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { GroupedLayers } from '..'
import { SideDrawerDivider } from '../../../components/SideDrawerDivider'
import { EmptySideDrawer } from '../../EmptyTab'
import { ElementAccordion } from './ElementAccordion'
import {
  informationColumn,
  riskSummaryFilterBanner,
  riskSummaryFooter,
  riskSummaryFooterInner,
  scrollingRiskBody,
} from './RiskSummary.styles'

export const RiskSummary = ({ groupedLayers }: { groupedLayers: GroupedLayers }) => {
  const theme = useTheme()
  const setSideDrawerInfoLayerFocus = useSetAtom(infoLayerFocusAtom)
  const { term_preference } = usePreferences()
  const { drawAreas } = useSelector((state: RootState) => state.riskMap)
  const { regionMasks } = useMap()
  const { elementFilters } = useLayers()
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useAtom(showFeedbackModalAtom)

  const { riskByAssetMultiHazard, riskByAssetPerHazard, isLoading } = useRiskFetcher()

  const hasElementFilters = useMemo(() => {
    return Object.values(elementFilters).some((filters) => Object.keys(filters).length > 0)
  }, [elementFilters])

  // Gather Layer info
  const hasAssets = useMemo(() => {
    return groupedLayers.assets.length > 0
  }, [groupedLayers])
  const hasHazards = useMemo(() => {
    return groupedLayers.hazards.length > 0
  }, [groupedLayers])
  const hadNoLayers = useRef(true)

  useEffect(() => {
    const checkHasLayers = Object.values(groupedLayers).some((layers) => layers.length > 0)
    // Open layer focus if layers were just added for the first time
    if (checkHasLayers && hadNoLayers.current) {
      hadNoLayers.current = false
      setSideDrawerInfoLayerFocus('risk')
    } else if (!checkHasLayers) {
      hadNoLayers.current = true
    }
  }, [setSideDrawerInfoLayerFocus, groupedLayers])

  let body = (
    <EmptySideDrawer
      text="No layers are visible on the map. Add layers to gain insights"
      type="element"
      buttonText="Add layers"
    />
  )

  const layerAccordionCommonProps = (index: number) => ({
    groupedLayers,
    animationDelay: index / 10,
  })
  if (hasAssets && hasHazards) {
    body = (
      <Box css={informationColumn({ theme })}>
        {groupedLayers.assets.map((layer, index) => (
          <Fragment key={index}>
            {index !== 0 && <SideDrawerDivider />}
            <ElementAccordion
              layer={layer}
              riskPerHazard={riskByAssetPerHazard[layer.type]}
              riskMultiHazard={riskByAssetMultiHazard[layer.type]}
              {...layerAccordionCommonProps(index)}
              isLoading={isLoading}
            />
          </Fragment>
        ))}
      </Box>
    )
  } else if (hasAssets) {
    body = (
      <EmptySideDrawer
        text={
          term_preference.hazard
            ? 'No hazards are visible on the map. Add hazards to gain insights'
            : 'No risk sources are visible on the map. Add risk sources to gain insights'
        }
        type="hazard"
        buttonText={term_preference.hazard ? 'Add Hazards' : 'Add Risk Sources'}
      />
    )
  } else if (hasHazards) {
    body = (
      <EmptySideDrawer
        text="No elements are visible on the map. Add elements to gain insights"
        type="element"
        buttonText="Add Elements"
      />
    )
  }

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        align-items: stretch;
      `}
    >
      <Box css={scrollingRiskBody({ theme })}>
        {hasAssets && hasHazards && (
          <>
            {!drawAreas.length && regionMasks && regionMasks.length > 0 && (
              <Typography css={riskSummaryFilterBanner({ theme })}>
                Filtering by selected regions
              </Typography>
            )}
            {drawAreas.length > 0 && (
              <Typography css={riskSummaryFilterBanner({ theme })}>
                Filtering by custom shape
              </Typography>
            )}
          </>
        )}
        {hasAssets && hasHazards && hasElementFilters && (
          <Typography css={riskSummaryFilterBanner({ theme })}>
            Filtering by element attributes
          </Typography>
        )}
        {body}
        <SideDrawerDivider />
        {hasAssets && hasHazards && (
          <Box css={riskSummaryFooter}>
            <Box css={riskSummaryFooterInner}>
              <Button
                size="medium"
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<ForumIcon />}
                sx={{
                  marginBottom: theme.spacing(2),
                }}
                onClick={() => setIsFeedbackModalOpen(!isFeedbackModalOpen)}
              >
                Submit Feedback
              </Button>
              <Box className="tutorial-risk-footer" />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
